import Navbar from "@/pages/home/components/Navbar";
import Footer from "@/pages/home/components/Footer";

const SECTIONS = [
  {
    title: "1. Information Collection",
    content: `When you visit the CCF Co-Creation Farm platform ("the Platform"), we may collect the following types of information:

1.1 Information You Provide
• Registration Information: When you create an account, join a farming program, or submit an inquiry form, we may collect your name, email address, phone number, region, and other personal information.
• Transaction Information: When you participate in dairy farming investments, we may collect your payment information, bank account details, and transaction records.
• Communication Information: When you contact customer support or participate in surveys, we may retain your communication records and feedback content.

1.2 Information Automatically Collected
• Device Information: Including IP address, browser type, operating system version, device identifiers.
• Usage Data: Including pages visited, time spent, click behavior, and browsing paths.
• Cookies and Similar Technologies: We use cookies and similar tracking technologies to improve user experience and analyze website traffic.

1.3 Information from Third Parties
• We may receive information about you from third-party partners such as partner farms and payment service providers, to complete your account profile and transaction records.`,
  },
  {
    title: "2. Information Use",
    content: `We use the personal information we collect for the following purposes:

2.1 Service Provision and Maintenance
• Creating and managing your farming account
• Processing your farming investment transactions and returns distribution
• Sending you important account-related notifications (e.g., returns deposited, farm updates)

2.2 Improvement and Optimization
• Analyzing user behavior to optimize platform functionality and user experience
• Developing and testing new features and services
• Conducting data analysis and market research

2.3 Communication and Marketing
• Sending you platform updates, event information, and marketing materials (you may unsubscribe at any time)
• Responding to your inquiries and customer support requests
• Inviting you to participate in user surveys and feedback collection

2.4 Security and Compliance
• Detecting and preventing fraud, abuse, and illegal activities
• Complying with applicable laws and regulatory requirements
• Enforcing the Platform's terms of service and related policies`,
  },
  {
    title: "3. Information Sharing",
    content: `We share your personal information with third parties only in the following circumstances:

3.1 Service Providers
We may share information with third parties that provide services on our behalf, including but not limited to:
• Payment processing providers (for processing transactions and returns distribution)
• Cloud storage and data analytics providers
• Customer support and technical maintenance providers
• Marketing and communications providers

These service providers may access your information only to the extent necessary to perform their services and are subject to equivalent confidentiality obligations.

3.2 Partner Farms
When you participate in a specific farm's farming program, we may need to provide necessary account information to that partner farm to ensure proper program execution and accurate returns distribution.

3.3 Legal Requirements
We may disclose your personal information in the following circumstances:
• To comply with applicable laws, regulations, court orders, or government requests
• To protect the rights, property, or safety of the Platform
• To detect, prevent, or address fraud, security, or technical issues

3.4 Business Transfers
If the Platform is involved in a merger, acquisition, or asset sale, your personal information may be transferred as part of the business assets. We will notify you through appropriate means.

3.5 With Your Consent
In circumstances other than those described above, we will obtain your explicit consent before sharing your personal information with third parties.`,
  },
  {
    title: "4. Data Security",
    content: `We take reasonable technical and administrative measures to protect your personal information:

4.1 Security Measures
• Using SSL/TLS encryption to protect data transmission
• Implementing access control mechanisms to limit access to personal information
• Conducting regular security assessments and vulnerability scans
• Encrypting sensitive information (such as payment information) at rest

4.2 Data Retention
We retain your personal information only for as long as necessary to fulfill the purposes described in this Privacy Policy, unless a longer retention period is required or permitted by law. When your personal information is no longer needed, we will securely delete or anonymize it.

4.3 Security Notice
Despite the security measures described above, please note that no method of internet transmission or electronic storage is 100% secure. We cannot guarantee absolute security.`,
  },
  {
    title: "5. Your Rights",
    content: `Under applicable data protection laws, you have the following rights:

5.1 Right of Access
You have the right to request access to a copy of the personal information we hold about you.

5.2 Right to Rectification
If you believe the personal information we hold about you is inaccurate or incomplete, you have the right to request correction.

5.3 Right to Erasure
In certain circumstances, you have the right to request deletion of your personal information, such as when the information is no longer necessary for the purposes for which it was collected.

5.4 Right to Restrict Processing
In certain circumstances, you have the right to request restriction of our processing of your personal information.

5.5 Right to Data Portability
You have the right to receive the personal information you have provided to us in a structured, commonly used, and machine-readable format.

5.6 Right to Withdraw Consent
Where we process your personal information based on your consent, you have the right to withdraw that consent at any time.

5.7 Exercising Your Rights
To exercise any of the above rights, please contact us using the contact information provided at the end of this Privacy Policy. We will respond to your request within a reasonable timeframe.`,
  },
  {
    title: "6. Cookie Policy",
    content: `6.1 What Are Cookies
Cookies are small text files that websites place on your device when you visit. They are widely used to make websites work more efficiently and provide information to website owners.

6.2 Cookies We Use
• Essential Cookies: These cookies are essential for the basic functionality of the website, such as user login and account management.
• Analytics Cookies: These cookies help us understand how visitors interact with the website, providing information about visit volume, bounce rate, and traffic sources.
• Functional Cookies: These cookies allow the website to remember your choices (such as language preferences) to deliver a more personalized experience.

6.3 Cookie Management
Most browsers allow you to control cookies through their settings. You may choose to reject all cookies or receive notification when a cookie is sent. However, please note that disabling cookies may affect your experience of certain features on the Platform.`,
  },
  {
    title: "7. Children's Privacy",
    content: `The Platform's services are not directed at individuals under the age of 18. We do not knowingly collect personal information from minors. If we discover that we have inadvertently collected personal information from a minor, we will take immediate steps to delete such information.

If you are a parent or guardian and believe your child has provided us with personal information, please contact us immediately so we can take appropriate action.`,
  },
  {
    title: "8. Privacy Policy Updates",
    content: `We may update this Privacy Policy from time to time. The updated Privacy Policy will be posted on this page with the last updated date indicated at the top.

For material changes, we will notify you through more prominent means, such as by email or by posting a notice on the Platform's homepage. We encourage you to review this Privacy Policy periodically to stay informed about how we protect your information.`,
  },
  {
    title: "9. Contact Us",
    content: `If you have any questions, comments, or complaints about this Privacy Policy, or wish to exercise your data rights, please contact us through the following channels:

• Email: cocreationfarm@gmail.com
• Address: 5445 DTC Pkwy, Greenwood Village, CO 80111, United States
• Online Support: Contact us through the Platform's in-app customer support system

We will respond to your request within a reasonable timeframe, typically within 30 business days.`,
  },
];

export default function Privacy() {
  return (
    <main className="w-full bg-background-50 text-foreground-950">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "WebPage",
        "name": "Privacy Policy - Co-Creation Farm CCF",
        "description": "Privacy Policy for CCF Co-Creation Farm, explaining how we collect, use, share, and protect your personal information, as well as your data rights.",
        "url": "%VITE_SITE_URL%/privacy",
        "isPartOf": {
          "@type": "WebSite",
          "name": "Co-Creation Farm",
          "url": "%VITE_SITE_URL%/"
        }
      }) }} />
      <Navbar />
      <div className="pt-20">
        <div className="w-full px-6 md:px-10 lg:px-14 py-16 md:py-20 bg-background-100">
          <div className="max-w-3xl mx-auto">
            <p className="text-sm text-foreground-500 mb-3">Last Updated: June 15, 2020</p>
            <h1
              className="text-3xl md:text-4xl lg:text-5xl font-heading font-medium tracking-tight"
              style={{ fontFamily: "Fraunces, serif" }}
            >
              Privacy Policy
            </h1>
            <p className="mt-4 text-foreground-600 leading-relaxed max-w-2xl">
              We value your privacy. This Privacy Policy explains how CCF Co-Creation Farm collects, uses, shares, and protects your personal information, as well as your rights regarding that information. By using the Platform, you agree to the practices described in this Privacy Policy.
            </p>
          </div>
        </div>

        <div className="w-full px-6 md:px-10 lg:px-14 py-16 md:py-20">
          <div className="max-w-3xl mx-auto space-y-16">
            {SECTIONS.map((section) => (
              <section key={section.title}>
                <h2
                  className="text-xl md:text-2xl font-heading font-medium mb-5"
                  style={{ fontFamily: "Fraunces, serif" }}
                >
                  {section.title}
                </h2>
                <div className="text-foreground-700 leading-relaxed text-sm md:text-base space-y-4 whitespace-pre-line">
                  {section.content}
                </div>
              </section>
            ))}
          </div>
        </div>
      </div>
      <Footer />
    </main>
  );
}